import isEmpty from "lodash/isEmpty";
import {
  CounterpartyProfileWrite,
  Payer,
  PayerEngagementCreateType,
  ResponseError
} from "@wingspanhq/wingspan-api-sdk";
import { linkRequestsApi, payerEngagementsApi, payersApi } from "@api";
import {
  queriesEngagements,
  queriesPayerEngagements,
  queriesPayerRows,
  queriesPayers
} from "@query";
import { useWSMutation, WSMutationConfig } from "../../../query/helpers";
import { WSServiceError } from "../../../utils/serviceHelper";
import { QUERY_PAYER_ROWS } from "../../../query/search/payer/queries/useQueryPayerRows";
import { QUERY_PAYER_ROWS_ALL } from "../../../query/search/payer/queries/useQueryPayerRowsAll";
import { QUERY_PAYER_ROWS_SUMMARY } from "../../../query/search/payer/queries/useQueryPayerRowsSummary";
import { ensureDefaultEngagement } from "../../EngagementTypes/utils/ensureDefaultEngagement";

export type CreateClientInput = {
  email: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  emailsCC?: string[];
};

/**
 * Maps the collected client fields onto the v3 profile. v1 stored one
 * `payerName` string; v3 wants the individual's first/last name separate, so
 * the modal now collects them as distinct fields and passes them through here
 * verbatim (no more lossy whitespace split).
 */
const buildProfile = (
  firstName?: string,
  lastName?: string,
  company?: string
): CounterpartyProfileWrite | undefined => {
  const profile: CounterpartyProfileWrite = {};

  const trimmedFirstName = firstName?.trim();
  const trimmedLastName = lastName?.trim();
  if (trimmedFirstName || trimmedLastName) {
    profile.individual = {
      firstName: trimmedFirstName || undefined,
      lastName: trimmedLastName || undefined
    };
  }

  const trimmedCompany = company?.trim();
  if (trimmedCompany) {
    profile.company = { doingBusinessAs: trimmedCompany };
  }

  return isEmpty(profile) ? undefined : profile;
};

const createLinkRequestIgnoringAlreadyRequested = async (
  payerId: string,
  targetEmail: string
): Promise<void> => {
  try {
    await linkRequestsApi.createPayerLinkRequest({
      payerId,
      linkRequestNestedCreate: { targetEmail }
    });
  } catch (error) {
    if (error instanceof ResponseError && error.response.status === 409) {
      return;
    }
    throw error;
  }
};

/**
 * Creates a client (payer) on v3, issues the Wingspan invite, and creates the
 * client's engagement.
 *
 * v1 `createPayer` was one call that recorded the payer relationship (with the
 * payee-owned `payerName` / `payerCompanyName` / `payerEmailCC`). v3 splits it:
 * `createPayer` takes only the lean payer (email + profile), and the invite —
 * modeled in the invite-flows spec as a `PayerPayeeLinkRequest` — is now an
 * explicit `createPayerLinkRequest`.
 *
 * `PayerCreate` carries no CC emails, but `PayerUpdate` does
 * (`notificationCcEmails`, WIN-15229 — the same field the edit modal writes), so
 * v1's create-time "Contact email CC" survives as a follow-up patch, issued only
 * when the modal collected any. The QuickBooks mapping v1 also collected here is
 * still dropped: `PayerUpdate` accepts `quickbooksCustomerId`/`quickbooksItemId`,
 * but the customer/item pickers need the QuickBooks connection surface, which is
 * still v1 (and therefore dead). See V3_MIGRATION_TRACKER.md.
 *
 * **The engagement is the third explicit step.** v1 created the payer engagement
 * under the hood; on v3 nothing does, and a client with no engagement cannot be
 * invoiced — `Invoice` requires a `payerEngagementId`, so the assignment picker
 * would be empty. The client side needs no per-client configuration (the payee
 * decides nothing about how the client engages it), so this attaches the
 * account's default engagement template silently rather than asking. The
 * contractor side is the opposite: `CreateContractorModal` requires an explicit
 * engagement choice.
 *
 * Reads stay on v1 (the Clients list/detail are still v1), so the mutation
 * explicitly invalidates the v1 payer caches; the v3 payer created this way
 * surfaces in the v1 `/search/payer-row` list once the backend indexes it.
 */
export const useCreateClient = (
  config?: WSMutationConfig<Payer, WSServiceError, CreateClientInput>
) =>
  useWSMutation<Payer, WSServiceError, CreateClientInput>(
    async input => {
      const payer = await payersApi.createPayer({
        payerCreate: {
          email: input.email,
          profile: buildProfile(input.firstName, input.lastName, input.company)
        }
      });

      // The engagement the client's invoices are billed against comes back
      // `Created` (not `Activated`) — the pickers treat both as selectable, see
      // `SelectEngagementForPayer`.
      const [, engagement] = await Promise.all([
        createLinkRequestIgnoringAlreadyRequested(payer.id!, input.email),
        ensureDefaultEngagement(),
        // Only patch when there is something to store, so the common case stays
        // at v1's request count.
        input.emailsCC?.length
          ? payersApi.updatePayer({
              payerId: payer.id!,
              payerUpdate: { notificationCcEmails: input.emailsCC }
            })
          : undefined
      ]);

      await payerEngagementsApi.createPayerEngagement({
        payerId: payer.id!,
        payerEngagementCreate: {
          engagementId: engagement.id!,
          type: PayerEngagementCreateType.COMMERCIAL,
          startDate: new Date()
        }
      });

      return payer;
    },
    {
      dependencies: [
        QUERY_PAYER_ROWS,
        QUERY_PAYER_ROWS_ALL,
        QUERY_PAYER_ROWS_SUMMARY,
        queriesPayers.listPayers.queryKeyBase,
        // The clients list and every client picker read the denormalized search
        // rows, so they must refresh too — without this the new client is missing
        // from a list that was already on screen until a hard reload.
        queriesPayerRows.searchPayerRows.queryKeyBase,
        queriesPayerEngagements.listPayerEngagements.queryKeyBase,
        queriesEngagements.listEngagements.queryKeyBase
      ],
      ...config
    }
  );
